import React, { useState, useCallback } from 'react'
import { Mail } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'

export interface Partner {
	id: string
	name: string
	email: string
	company?: string
	title?: string
	image?: string
	owner?: boolean
	emailOption: string
}

interface CommunicationPartnersCNProps {
	open: boolean
	onClose: () => void
	partners: Partner[]
	assetName?: string
}

type EmailOption = 'primary' | 'cc' | 'bcc' | ''

const CommunicationPartnersCN = ({ open, onClose, partners, assetName = '' }: CommunicationPartnersCNProps) => {
	const [selections, setSelections] = useState<Record<string, EmailOption>>({})

	const handleOptionChange = useCallback((partnerId: string, option: EmailOption) => {
		setSelections(prev => ({
			...prev,
			[partnerId]: prev[partnerId] === option ? '' : option,
		}))
	}, [])

	const handleCompose = () => {
		const selected = partners.filter(p => selections[p.id])
		if (selected.length === 0) return

		const primary = selected.filter(p => selections[p.id] === 'primary').map(p => p.email)
		const cc = selected.filter(p => selections[p.id] === 'cc').map(p => p.email)
		const bcc = selected.filter(p => selections[p.id] === 'bcc').map(p => p.email)

		let mailto = `mailto:${primary.join(';')}`
		const params: string[] = []
		if (cc.length) params.push(`cc=${cc.join(';')}`)
		if (bcc.length) params.push(`bcc=${bcc.join(';')}`)
		if (assetName) params.push(`Subject=${encodeURIComponent(assetName)}`)
		if (params.length) mailto += `?${params.join('&')}`

		window.location.href = mailto
		setSelections({})
		onClose()
	}

	const hasSelection = Object.values(selections).some(v => v)

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-4xl p-0 overflow-hidden">
				<DialogHeader className="px-5 pt-5 pb-0">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<Mail className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Partner Communication</DialogTitle>
							<DialogDescription>
								Select email routing for each partner, then click Compose to open your email client.
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-5 py-4">
					<Table>
						<TableHeader>
							<TableRow className="border-b-2 border-[#0080fe] hover:bg-transparent">
								<TableHead className="w-10" />
								<TableHead className="text-gray-700 text-xs font-semibold">Partner</TableHead>
								<TableHead className="text-gray-700 text-xs font-semibold">Company</TableHead>
								<TableHead className="text-gray-700 text-xs font-semibold">Email</TableHead>
								<TableHead className="text-gray-700 text-xs font-semibold text-center w-16">To</TableHead>
								<TableHead className="text-gray-700 text-xs font-semibold text-center w-16">CC</TableHead>
								<TableHead className="text-gray-700 text-xs font-semibold text-center w-16">BCC</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{partners.map(partner => {
								const selected = selections[partner.id] || ''
								return (
									<TableRow key={partner.id} className="hover:bg-gray-50">
										<TableCell className="py-1.5">
											<Avatar className="h-7 w-7">
												<AvatarImage src={partner.image} />
												<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
													{(partner.name || '?').charAt(0).toUpperCase()}
												</AvatarFallback>
											</Avatar>
										</TableCell>
										<TableCell className="py-1.5 text-sm font-medium">{partner.name}</TableCell>
										<TableCell className="py-1.5 text-sm text-gray-500">{partner.company}</TableCell>
										<TableCell className="py-1.5 text-sm text-gray-500">{partner.email}</TableCell>
										<TableCell className="py-1.5 text-center">
											<input
												type="radio"
												checked={selected === 'primary'}
												onChange={() => handleOptionChange(partner.id, 'primary')}
												name={`email-${partner.id}`}
												aria-label={`${partner.name} as primary recipient`}
												className="w-4 h-4 text-[#0080fe] cursor-pointer accent-[#0080fe]"
											/>
										</TableCell>
										<TableCell className="py-1.5 text-center">
											<input
												type="radio"
												checked={selected === 'cc'}
												onChange={() => handleOptionChange(partner.id, 'cc')}
												name={`email-${partner.id}`}
												aria-label={`${partner.name} as CC`}
												className="w-4 h-4 text-[#0080fe] cursor-pointer accent-[#0080fe]"
											/>
										</TableCell>
										<TableCell className="py-1.5 text-center">
											<input
												type="radio"
												checked={selected === 'bcc'}
												onChange={() => handleOptionChange(partner.id, 'bcc')}
												name={`email-${partner.id}`}
												aria-label={`${partner.name} as BCC`}
												className="w-4 h-4 text-[#0080fe] cursor-pointer accent-[#0080fe]"
											/>
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>
				</div>

				<div className="flex justify-end px-5 py-4 border-t border-gray-200">
					<Button variant="ghost" isOutline className="mr-2" onClick={onClose}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleCompose} disabled={!hasSelection}>
						<Mail className="w-4 h-4 mr-2" />
						Compose
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default CommunicationPartnersCN
